import Money from "@dashboard/components/Money";
import { Text } from "@saleor/macaw-ui-next";
import React from "react";
import { defineMessages, useIntl } from "react-intl";

// ponytail: one renderer for every amount the three WSM sections show, so a fee,
// a kit discount and a tier price cannot drift into three shapes again.
// Ceiling: percentages are formatted by hand rather than through Intl, because
// the API hands back a plain decimal string with no locale attached.
// Upgrade path: Intl.NumberFormat(locale, { style: "percent" }) once the API
// says whether 10.00 means 10% or 0.1.

export const wsmMoneyMessages = defineMessages({
  noCurrency: {
    id: '5U2Wdu',
    defaultMessage: "No currency",
    description: "amount stored without a currency",
  },
  noAmount: {
    id: 'oT5vH9',
    defaultMessage: "None",
    description: "no amount stored",
  },
});

export type WsmAmountBasis = "fixed" | "percent";

export type WsmAmountView =
  | { kind: "money"; amount: number; currency: string }
  | { kind: "percent"; text: string }
  | { kind: "noCurrency"; text: string }
  | { kind: "none" };

/** The API hands money back as an exact decimal string. Anything unparseable is "none". */
export const parseWsmAmount = (amount: string | number | null | undefined): number | null => {
  if (amount === null || amount === undefined || amount === "") {
    return null;
  }

  const parsed = typeof amount === "number" ? amount : Number(amount);

  return Number.isFinite(parsed) ? parsed : null;
};

/** 10.00 reads as "10%", 12.50 as "12.5%". Trailing zeros are noise on a percentage. */
export const formatWsmPercent = (amount: number): string =>
  `${Number(amount.toFixed(2))}%`;

/** The one decision: what this amount is, before anything renders it. */
export const wsmAmountView = (
  amount: string | number | null | undefined,
  currency: string | null | undefined,
  basis: WsmAmountBasis = "fixed",
): WsmAmountView => {
  const parsed = parseWsmAmount(amount);

  if (parsed === null) {
    return { kind: "none" };
  }

  if (basis === "percent") {
    return { kind: "percent", text: formatWsmPercent(parsed) };
  }

  if (!currency) {
    return { kind: "noCurrency", text: parsed.toFixed(2) };
  }

  return { kind: "money", amount: parsed, currency };
};

export interface WsmAmountProps {
  amount: string | number | null | undefined;
  currency?: string | null;
  basis?: WsmAmountBasis;
}

export const WsmAmount = ({ amount, currency, basis = "fixed" }: WsmAmountProps) => {
  const intl = useIntl();
  const view = wsmAmountView(amount, currency, basis);

  switch (view.kind) {
    case "money":
      return <Money money={{ amount: view.amount, currency: view.currency }} />;
    case "percent":
      return (
        <Text size={3} data-test-id="wsm-amount-percent">
          {view.text}
        </Text>
      );
    case "noCurrency":
      return (
        <Text size={3} color="default2" data-test-id="wsm-amount-no-currency">
          {view.text} {intl.formatMessage(wsmMoneyMessages.noCurrency)}
        </Text>
      );
    default:
      return (
        <Text size={3} color="default2" data-test-id="wsm-amount-none">
          {intl.formatMessage(wsmMoneyMessages.noAmount)}
        </Text>
      );
  }
};

WsmAmount.displayName = "WsmAmount";
